import React, { useEffect, useState, useMemo } from 'react';
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Button,
	Typography,
	Box,
	CircularProgress,
	Tooltip,
	Link,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import moment, { Moment } from 'moment';
import { Link as RouterLink } from 'react-router-dom';
import { getFirstSolvers, downloadBalloonCsv } from '../../../core/contests/services';
import { Contest, FirstSolverResponse } from '../../../core/contests/types';
import { enqueueSnackbar } from 'notistack';

interface TimeInterval {
	start: Moment;
	end: Moment;
	label: string;
}

interface BalloonViewProps {
	contest: Contest;
}

export default function BalloonView({ contest }: BalloonViewProps) {
	const { id: contestId, slug: contestSlug, starts_at: startsAt, ends_at: endsAt } = contest;
	const [firstSolvers, setFirstSolvers] = useState<FirstSolverResponse[]>([]);
	const [loadingFirstSolvers, setLoadingFirstSolvers] = useState(false);
	const [downloadingInterval, setDownloadingInterval] = useState<string | null>(null);

	useEffect(() => {
		if (contestId) {
			setLoadingFirstSolvers(true);
			getFirstSolvers(contestId)
				.then((response) => {
					setFirstSolvers(response.results);
				})
				.catch((error) => {
					console.error('Error fetching first solvers:', error);
					enqueueSnackbar('Failed to fetch first solvers', {
						variant: 'error',
						autoHideDuration: 3000,
					});
				})
				.finally(() => {
					setLoadingFirstSolvers(false);
				});
		}
	}, [contestId]);

	const timeIntervals = useMemo((): TimeInterval[] => {
		if (!startsAt || !endsAt) return [];

		const start = moment(startsAt);
		const end = moment(endsAt);
		const now = moment();
		const intervals: TimeInterval[] = [];

		let intervalStart = start.clone();
		while (intervalStart.isBefore(end)) {
			const intervalEnd = moment.min(intervalStart.clone().add(30, 'minutes'), end);

			// Only include intervals that are not in the future
			if (intervalStart.isBefore(now)) {
				intervals.push({
					start: intervalStart.clone(),
					end: intervalEnd.clone(),
					label: `${intervalStart.format('HH:mm')} - ${intervalEnd.format('HH:mm')}`,
				});
			}

			intervalStart = intervalEnd.clone();
		}

		return intervals;
	}, [startsAt, endsAt]);

	const handleDownloadCsv = async (interval: TimeInterval) => {
		if (!contestId) return;

		const intervalKey = interval.label;
		setDownloadingInterval(intervalKey);

		try {
			const blob = await downloadBalloonCsv(contestId, interval.start.toISOString(), interval.end.toISOString());

			// Create download link
			//  this need to this way to work properly :D
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', `balloons_${interval.start.format('HH-mm')}_${interval.end.format('HH-mm')}.csv`);
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);

			enqueueSnackbar('CSV downloaded successfully', {
				variant: 'success',
				autoHideDuration: 3000,
			});
		} catch (error) {
			console.error('Error downloading CSV:', error);
			enqueueSnackbar('Failed to download CSV', {
				variant: 'error',
				autoHideDuration: 3000,
			});
		} finally {
			setDownloadingInterval(null);
		}
	};

	return (
		<Box sx={{ mt: 2 }}>
			{/* First Solvers Table */}
			<Typography variant='h6' gutterBottom>
				First Solvers
			</Typography>
			<TableContainer component={Paper} sx={{ mb: 4 }}>
				<Table size='small'>
					<TableHead>
						<TableRow>
							<TableCell>Problem</TableCell>
							<TableCell>Solved By</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{loadingFirstSolvers ? (
							<TableRow>
								<TableCell colSpan={2} align='center'>
									<CircularProgress size={24} />
								</TableCell>
							</TableRow>
						) : firstSolvers.length === 0 ? (
							<TableRow>
								<TableCell colSpan={2} align='center'>
									No first solvers data available
								</TableCell>
							</TableRow>
						) : (
							firstSolvers.map((solver) => (
								<TableRow key={solver.problem_order}>
									<TableCell>
										<Link
											component={RouterLink}
											to={`/contests/${contestSlug}/submissions?problem_set=${solver.problem_set_id}&submitted_by=&response=ACCEPTED`}
											target='_blank'
											rel='noopener noreferrer'
											underline='hover'
										>
											{solver.order_character}
										</Link>
									</TableCell>
									<TableCell>
										<Tooltip title={solver.username} arrow>
											<span style={{ cursor: 'pointer' }}>{solver.first_name}</span>
										</Tooltip>
									</TableCell>
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</TableContainer>

			{/* Time Intervals Table */}
			<Typography variant='h6' gutterBottom>
				Balloon CSV Downloads
			</Typography>
			<TableContainer component={Paper}>
				<Table size='small'>
					<TableHead>
						<TableRow>
							<TableCell>Time Interval</TableCell>
							<TableCell align='right'>Action</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{timeIntervals.length === 0 ? (
							<TableRow>
								<TableCell colSpan={2} align='center'>
									No time intervals available
								</TableCell>
							</TableRow>
						) : (
							timeIntervals.map((interval) => (
								<TableRow key={interval.label}>
									<TableCell>{interval.label}</TableCell>
									<TableCell align='right'>
										<Button
											variant='outlined'
											size='small'
											startIcon={
												downloadingInterval === interval.label ? <CircularProgress size={16} /> : <DownloadIcon />
											}
											onClick={() => handleDownloadCsv(interval)}
											disabled={downloadingInterval === interval.label}
										>
											Download CSV
										</Button>
									</TableCell>
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</TableContainer>
		</Box>
	);
}
